import posthog from "posthog-js";
import { supabase } from "@/integrations/supabase/client";

export type ProblemFrequency = "daily" | "weekly" | "monthly" | "rare" | "unknown";

export type ValidationState =
  | "none"
  | "interviewed"
  | "email_collected"
  | "demo_requests"
  | "mvp_no_users"
  | "mvp_with_users"
  | "paying_users";

export type TestChannel =
  | "linkedin"
  | "email"
  | "phone"
  | "community"
  | "instagram_dm"
  | "whatsapp"
  | "cold_walk_in"
  | "other"
  | "unknown";

export type CurrentBehaviourChoice =
  | "none"
  | "workaround"
  | "workaround_free_tools"
  | "competitor"
  | "paid_solution"
  | "unknown";

export type ProblemUrgencyCost = "low" | "medium" | "high" | "critical" | "unknown";

export type RealityCheckInputs = {
  // Required
  idea: string;
  target: string;
  problem: string;
  problem_frequency: ProblemFrequency | "";
  problem_urgency_cost: ProblemUrgencyCost | "";
  current_behaviour_choice: CurrentBehaviourChoice | "";
  current_solution: string;
  solution: string;
  validation_state: ValidationState | "";
  test_channel: TestChannel | "";
  // Conditional (required only when test_channel === "other")
  test_channel_other: string;
  // Optional
  competitors: string;
  differentiation: string;
  business_model: string;
};

export type DiagnosisArea = {
  area: "Problema" | "Target" | "Comportamento attuale" | "Evidenze" | "Canale / Testabilità";
  level: "forte" | "medio" | "debole";
  note: string;
};

export type RealityCheckResult = {
  idea_summary: string;
  target_summary: string;
  headline: string;
  verdict_type: string; // internal; never shown raw
  capped: boolean;
  prudent_note: string | null;
  diagnosis: DiagnosisArea[];
  main_risk: string;
  experiment_48h: string;
  why_this_experiment: string;
  success_metric: string;
  minimum_success_threshold: string;
  cta_angle: string;
  // Optional advisor fields (added for richer post-result guidance).
  // Optional in the type for backwards-compatibility with results saved
  // in sessionStorage before these fields were introduced.
  verdict?: "CONTINUA" | "ITERA" | "RIPENSA";
  verdict_reason?: string;
  failure_reason?: string;
  contact_script?: string;
  readiness_level?: "Alto" | "Medio" | "Basso";
};

export type StoredRealityCheck = {
  id: string;
  result: RealityCheckResult;
};

const RESULT_KEY = "v2_reality_check_result";
const DRAFT_KEY = "v2_rc_form_draft";
const ANON_TOKEN_KEY = "v2_anonymous_token";

export const EMPTY_INPUTS: RealityCheckInputs = {
  idea: "",
  target: "",
  problem: "",
  problem_frequency: "",
  problem_urgency_cost: "",
  current_behaviour_choice: "",
  current_solution: "",
  solution: "",
  validation_state: "",
  test_channel: "",
  test_channel_other: "",
  competitors: "",
  differentiation: "",
  business_model: "",
};

export function getAnonymousToken(): string {
  try {
    let t = localStorage.getItem(ANON_TOKEN_KEY);
    if (!t) {
      t = crypto.randomUUID();
      localStorage.setItem(ANON_TOKEN_KEY, t);
    }
    return t;
  } catch {
    return crypto.randomUUID();
  }
}

// --- Result persistence (sessionStorage) ---
export function saveResult(data: StoredRealityCheck) {
  try {
    sessionStorage.setItem(RESULT_KEY, JSON.stringify(data));
  } catch {}
}
export function loadResult(): StoredRealityCheck | null {
  try {
    const raw = sessionStorage.getItem(RESULT_KEY);
    return raw ? (JSON.parse(raw) as StoredRealityCheck) : null;
  } catch {
    return null;
  }
}
export function clearResult() {
  try {
    sessionStorage.removeItem(RESULT_KEY);
  } catch {}
}

// --- Draft persistence (sessionStorage) ---
export type FormDraft = {
  step: number;
  inputs: RealityCheckInputs;
  email: string;
  consent: boolean;
};

export function saveDraft(d: FormDraft) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d));
  } catch {}
}
const VALID_URGENCIES: ProblemUrgencyCost[] = ["low", "medium", "high", "critical", "unknown"];
const VALID_BEHAVIOURS: CurrentBehaviourChoice[] = [
  "none",
  "workaround",
  "workaround_free_tools",
  "competitor",
  "paid_solution",
  "unknown",
];
const VALID_CHANNELS: TestChannel[] = [
  "linkedin",
  "email",
  "phone",
  "community",
  "instagram_dm",
  "whatsapp",
  "cold_walk_in",
  "other",
  "unknown",
];
const VALID_FREQUENCIES: ProblemFrequency[] = ["daily", "weekly", "monthly", "rare", "unknown"];
const VALID_VALIDATION_STATES: ValidationState[] = [
  "none",
  "interviewed",
  "email_collected",
  "demo_requests",
  "mvp_no_users",
  "mvp_with_users",
  "paying_users",
];

function isDraftCompatible(d: any): boolean {
  if (!d || typeof d !== "object" || !d.inputs || typeof d.inputs !== "object") return false;
  const i = d.inputs;
  // Reject if old draft has any enum value not in the new enums (excluding empty string = "not chosen yet").
  const checks: Array<[any, readonly string[]]> = [
    [i.problem_urgency_cost, VALID_URGENCIES],
    [i.current_behaviour_choice, VALID_BEHAVIOURS],
    [i.test_channel, VALID_CHANNELS],
    [i.problem_frequency, VALID_FREQUENCIES],
    [i.validation_state, VALID_VALIDATION_STATES],
  ];
  for (const [val, allowed] of checks) {
    if (val !== undefined && val !== "" && !allowed.includes(val)) return false;
  }
  // Required new keys must exist (even if empty string) so the form can mount safely.
  const requiredKeys = [
    "problem_urgency_cost",
    "current_behaviour_choice",
    "test_channel_other",
  ];
  for (const k of requiredKeys) {
    if (!(k in i)) return false;
  }
  return true;
}

export function loadDraft(): FormDraft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FormDraft;
    if (!isDraftCompatible(parsed)) {
      sessionStorage.removeItem(DRAFT_KEY);
      return null;
    }
    return parsed;
  } catch {
    try {
      sessionStorage.removeItem(DRAFT_KEY);
    } catch {}
    return null;
  }
}
export function clearDraft() {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {}
}

export function mapErrorToItalian(code?: string): string {
  switch (code) {
    case "rate_limit":
      return "Troppe richieste in poco tempo. Riprova tra qualche minuto.";
    case "payment_required":
      return "Servizio temporaneamente non disponibile. Riprova più tardi.";
    case "invalid_ai_output":
    case "ai_error":
    case "ai_unreachable":
      return "Non siamo riusciti a generare il tuo Reality Check. Riprova tra qualche minuto.";
    case "invalid_email":
      return "Inserisci un'email valida.";
    case "ai_not_configured":
    case "db_insert_failed":
    case "db_update_failed":
      return "Errore interno. Riprova più tardi.";
    default:
      if (code?.startsWith("invalid_input_") || code?.startsWith("invalid_")) {
        return "Compila tutti i campi richiesti.";
      }
      return "Si è verificato un errore. Riprova tra qualche minuto.";
  }
}

export async function submitRealityCheck(payload: {
  email: string;
  marketing_consent: boolean;
  inputs: RealityCheckInputs;
}): Promise<StoredRealityCheck> {
  const anonymous_token = getAnonymousToken();

  const { data, error } = await supabase.functions.invoke("v2-reality-check", {
    body: {
      email: payload.email,
      marketing_consent: payload.marketing_consent,
      inputs: payload.inputs,
      anonymous_token,
    },
  });

  if (error) {
    let code: string | undefined;
    try {
      const ctx = (error as any).context;
      if (ctx && typeof ctx.json === "function") {
        const j = await ctx.json();
        code = j?.error;
      }
    } catch {}
    throw new Error(mapErrorToItalian(code));
  }

  if (!data?.id || !data?.result) {
    throw new Error(mapErrorToItalian("invalid_ai_output"));
  }

  const stored: StoredRealityCheck = { id: data.id, result: data.result };
  saveResult(stored);
  try {
    posthog.capture("rc_submitted", {
      verdict: stored.result.verdict ?? null,
      verdict_type: stored.result.verdict_type ?? null,
    });
  } catch {}
  try {
    sessionStorage.setItem("v2_rc_email", payload.email.trim());
  } catch {}
  // Clean up draft after successful submit so a new RC starts blank.
  clearDraft();
  return stored;

}
